import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Newspaper, X, Copy, Check, CaretDown } from "@phosphor-icons/react";
import { fetchSitReport, fetchNifcBriefing, fetchNwccBrief } from "../lib/api";
import { useCounty } from "../lib/CountyContext";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "./InlineShareButton";
import { loadMorningPostText, loadEveningPostText } from "./MorningPostButton";

function nifcText(d) {
  const s = d.stats || {};
  const wa = d.washington || {};
  const lines = [
    `🇺🇸 NIFC NATIONAL FIRE REPORT — ${d.date || ""}`.trim(),
    `National Preparedness Level ${d.preparedness_level ?? "?"}`,
    "",
    `LARGE FIRES: ${s.total_large_fires ?? "?"} active · ${s.active_acres ?? "?"} acres`,
  ];
  if (s.new_large_fires) lines.push(`New large fires: ${s.new_large_fires} · Contained yesterday: ${s.fires_contained ?? 0}`);
  if (s.ytd_fires) lines.push(`Year to date: ${s.ytd_fires} fires / ${s.ytd_acres} acres`);
  if (wa.fires_count) {
    lines.push("");
    lines.push(`WASHINGTON: ${wa.fires_count} large fires · ${wa.acres} acres${wa.new ? ` · ${wa.new} new` : ""}`);
    (wa.fires || []).slice(0, 6).forEach((f) => {
      let l = `• ${f.name}`;
      if (f.size) l += ` — ${f.size} ac`;
      if (f.pct_contained != null) l += `, ${f.pct_contained}% contained`;
      lines.push(l);
    });
  }
  if (d.weather) {
    lines.push("");
    lines.push(`WEATHER: ${d.weather}`);
  }
  lines.push("");
  lines.push(`Full national report: ${d.imsr_url || d.source_url || "https://www.nifc.gov"}`);
  return lines.join("\n");
}

function nwccText(d) {
  const lines = [
    `🌲 NWCC MORNING BRIEF — ${d.date || ""}`.trim(),
    `NW Preparedness Level ${d.pl?.current ?? "?"} (National ${d.pl?.national ?? "?"})`,
    "",
  ];
  if (d.nw_fires != null) lines.push(`New fires yesterday: ${d.nw_fires} (WA ${d.wa_fires ?? "?"} / OR ${d.or_fires ?? "?"}) · ${d.nw_acres} acres`);
  if (d.acres_growth) lines.push(`Growth on large incidents: +${d.acres_growth} acres`);
  if (d.new_large_incidents) lines.push(`New large incidents: ${d.new_large_incidents}`);
  const waFires = (d.incidents || []).filter((i) => i.state === "WA").slice(0, 6);
  if (waFires.length) {
    lines.push("");
    lines.push("WASHINGTON LARGE INCIDENTS:");
    waFires.forEach((f) => {
      let l = `• ${f.name}`;
      if (f.acres) l += ` — ${f.acres} ac`;
      if (f.pct_contained != null) l += `, ${f.pct_contained}% contained`;
      if (f.location) l += ` (${f.location})`;
      lines.push(l);
    });
  }
  lines.push("");
  lines.push(`Full NWCC brief: ${d.source_url || "https://gacc.nifc.gov/nwcc/"}`);
  return lines.join("\n");
}

export default function ReportsMenu() {
  const { county, current } = useCounty();
  const [menuOpen, setMenuOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const REPORTS = {
    morning: {
      title: "Morning Post",
      build: () => loadMorningPostText(),
    },
    evening: {
      title: "Evening Post",
      build: () => loadEveningPostText(),
    },
    nifc: {
      title: "NIFC Morning Report",
      build: async () => `${nifcText(await fetchNifcBriefing())}\n\nLive WA map & alerts: ${shareOrigin()}`,
    },
    nwcc: {
      title: "NWCC Morning Report",
      build: async () => `${nwccText(await fetchNwccBrief())}\n\nLive WA map & alerts: ${shareOrigin()}`,
    },
    sit: {
      title: "WA State Fire Situation Report",
      build: async () => `${(await fetchSitReport()).text}\n\nLive statewide map & alerts:\n${shareOrigin()}/?county=washington`,
    },
  };

  const openReport = async (key) => {
    setMenuOpen(false);
    setReport(REPORTS[key]);
    setLoading(true);
    setText("");
    try {
      setText(await REPORTS[key].build());
    } catch {
      setText("Report unavailable right now — please try again in a minute.");
    } finally {
      setLoading(false);
    }
  };

  // Deep link: /?report=morning|evening auto-opens that report (used by the
  // scheduled "post ready" pushes).
  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get("report");
    if (key && REPORTS[key]) openReport(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const modal = report ? createPortal(
    <div
      data-testid="report-modal"
      className="fixed inset-0 z-[1400] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setReport(null)}
    >
      <div
        className="w-full max-w-xl max-h-[90vh] bg-tac-bg border border-tac-border rounded-sm shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-tac-border">
          <span className="text-[12px] font-bold uppercase tracking-[0.15em] text-tac-text flex items-center gap-2">
            <Newspaper size={14} weight="fill" className="text-tac-primary" /> {report.title}
          </span>
          <button type="button" data-testid="report-modal-close" onClick={() => setReport(null)} className="text-tac-dim hover:text-tac-text">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto tac-scroll">
          {loading ? (
            <div className="text-[12px] font-mono-tac text-tac-dim py-8 text-center">Building the report…</div>
          ) : (
            <textarea
              data-testid="report-modal-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={16}
              className="w-full bg-tac-surface border border-tac-border rounded-sm p-3 text-[13px] leading-relaxed text-tac-text font-mono-tac focus:outline-none focus:border-tac-primary resize-y"
            />
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-tac-border">
          <span className="text-[10px] font-mono-tac text-tac-faint">{text.length} chars · edit freely before posting</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="report-modal-copy"
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-tac-border text-tac-text hover:border-tac-primary transition-colors"
            >
              {copied ? <Check size={12} weight="bold" className="text-green-400" /> : <Copy size={12} weight="bold" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <InlineShareButton
              testid="report-modal-share"
              url={shareOrigin()}
              title={report.title}
              text={text}
              caption={text}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="reports-menu-btn"
        title="Ready-to-post fire reports — Morning Post, NIFC, NWCC, WA Sit Report"
        onClick={() => setMenuOpen((v) => !v)}
        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-tac-border text-tac-dim hover:text-tac-text hover:border-tac-borderBright transition-colors whitespace-nowrap"
      >
        <Newspaper size={13} weight="bold" />
        <span className="hidden min-[1360px]:inline whitespace-nowrap">Reports</span>
        <CaretDown size={10} weight="bold" />
      </button>
      {menuOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-tac-bg border border-tac-border rounded-sm shadow-2xl z-[1300] py-1">
          {Object.entries(REPORTS).map(([key, r]) => (
            <button
              key={key}
              type="button"
              data-testid={`reports-menu-${key}`}
              onClick={() => openReport(key)}
              className="w-full text-left px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text hover:bg-tac-surface transition-colors"
            >
              {r.title}
            </button>
          ))}
          <a
            href="/season-recap"
            data-testid="reports-menu-season-recap"
            className="block w-full text-left px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text hover:bg-tac-surface transition-colors border-t border-tac-border/60"
          >
            Season Recap
          </a>
        </div>
      )}
      {modal}
    </div>
  );
}
